// Lightweight auth context — tracks the current user across the app.
// On mount, tries /api/auth/me with the stored token. Login/register helpers
// set the token and refresh the user.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, ApiError, refreshSession } from "./api";
import {
  applyTokenResponse,
  clearToken,
  getToken,
  getTokenExpiresAt,
  TOKEN_STORAGE_KEY,
  type CurrentUser,
  type TokenResponse,
} from "./auth";

interface AuthState {
  user: CurrentUser | null;
  ready: boolean;                // false until the initial /me probe settles
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string, password: string, name?: string, inviteToken?: string,
  ) => Promise<{ pending: boolean; message?: string }>;
  signInWithGoogle: (credential: string) => Promise<void>;
  linkGoogle: (credential: string) => Promise<void>;
  unlinkGoogle: () => Promise<void>;
  signOut: () => void;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setReady(true);
      return;
    }
    try {
      const me = await api.get<CurrentUser>("/api/auth/me");
      setUser(me);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        clearToken();
      }
      setUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Cross-tab auth sync (2026-07 E2E fix). A refresh/sign-in/sign-out in one tab
  // rewrites the shared localStorage token; the `storage` event is how every OTHER
  // tab hears about it. Requests already read the current token at call time
  // (auth.getToken has no memo), so here we only reconcile the visible auth STATE:
  // token gone → show signed-out immediately (never a tab full of silent 401s /
  // stuck spinners); token appeared or rotated → re-probe /me to adopt the session.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      // e.key === null means localStorage.clear(); otherwise only our token key matters.
      if (e.key !== null && e.key !== TOKEN_STORAGE_KEY) return;
      if (!getToken()) {
        setUser(null);
        return;
      }
      void refresh();
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refresh]);

  // Proactively refresh ~60s before the short-lived access token expires so
  // long Studio sessions (autosave, spec-completeness polling) never hit 401.
  useEffect(() => {
    if (!user) return;
    const tick = () => {
      const exp = getTokenExpiresAt();
      if (!exp || !getToken()) return;
      if (exp - Date.now() <= 60_000) void refreshSession();
    };
    tick();
    const id = window.setInterval(tick, 30_000);
    return () => window.clearInterval(id);
  }, [user]);

  const signIn = useCallback(async (email: string, password: string) => {
    const tok = await api.post<TokenResponse>(
      "/api/auth/login",
      { email, password },
      { auth: false },
    );
    applyTokenResponse(tok);
    await refresh();
  }, [refresh]);

  const signUp = useCallback(async (
    email: string, password: string, name?: string, inviteToken?: string,
  ) => {
    // Register returns a token session, or — when invite-only is on and the signup needs
    // admin approval — a 202 { status: "pending" } with no token.
    const res = await api.post<TokenResponse | { status: string; message?: string }>(
      "/api/auth/register",
      { email, password, name, invite_token: inviteToken },
      { auth: false },
    );
    if (res && "access_token" in res) {
      applyTokenResponse(res as TokenResponse);
      await refresh();
      return { pending: false };
    }
    return { pending: true, message: (res as { message?: string }).message };
  }, [refresh]);

  const signInWithGoogle = useCallback(async (credential: string) => {
    const tok = await api.post<TokenResponse>(
      "/api/auth/google",
      { credential },
      { auth: false },
    );
    applyTokenResponse(tok);
    await refresh();
  }, [refresh]);

  // Connect/disconnect Google for the already-signed-in user. No token changes,
  // so just re-fetch /me to pick up the new link state (same pattern as signIn).
  const linkGoogle = useCallback(async (credential: string) => {
    await api.post("/api/auth/google/link", { credential });
    await refresh();
  }, [refresh]);

  const unlinkGoogle = useCallback(async () => {
    await api.post("/api/auth/google/unlink");
    await refresh();
  }, [refresh]);

  const signOut = useCallback(() => {
    clearToken();
    setUser(null);
    void api.post("/api/auth/logout", undefined, { auth: false }).catch(() => {});
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, ready, signIn, signUp, signInWithGoogle, linkGoogle, unlinkGoogle, signOut }),
    [user, ready, signIn, signUp, signInWithGoogle, linkGoogle, unlinkGoogle, signOut],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}
